import React, { useState } from 'react';
import { Stack, Button, Typography } from '@mui/material';
import { setAlert } from '../../helpers/alerts';
import ConfirmationDialog from '../dialog/ConfirmationDialog';
import { apiTriggerStep } from '../../api/releases';
import { deleteReleaseWithAlert, setReleaseTagWithAlert } from './releaseActions';
import { checkTestsAreValid } from './releaseTestGate';

export default function ActionsForRelease(props) {
    const { release, reloadData, direction, user } = props;
    const [dialog, setDialog] = useState({
        open: false,
        title: "",
        message: "",
        onConfirm: () => { },
        onCancel: () => { },
    });

    const handleAction = (uuid, action) => {
        if (action === "delete") {
            deleteReleaseWithAlert(uuid, {
                reloadData,
                successTitle: action,
                errorTitle: action,
            }).catch(() => {});
            return;
        }

        if (action === "branch") {
            let payload = {
                stage: "branching",
            };
            apiTriggerStep(uuid, payload).then((r) => {
                if (reloadData) {
                    reloadData();
                }
                setAlert(action, `Creating branches for release "${r.release.version}"`, 'success');
            }).catch((e) => {
                setAlert("Branching error", e, 'danger');
            });
            return;
        }

        if (action === "run_test") {
            let payload = {
                stage: "testing",
            };
            apiTriggerStep(uuid, payload).then((r) => {
                if (reloadData) {
                    reloadData();
                }
                setAlert(action, `Running tests for release "${r.release.version}"`, 'success');
            }).catch((e) => {
                setAlert("Test run error", e, 'danger');
            });
            return;
        }
        if (action === "release") {
            let payload = {
                stage: "releasing",
            };
            apiTriggerStep(uuid, payload).then((r) => {
                if (reloadData) {
                    reloadData();
                }
                setAlert(action, `Releasing version "${r.release.version}"`, 'success');
            }).catch((e) => {
                // if is a string, it's an error message
                if (typeof e === 'string') {
                    return setAlert("Release error", e, 'danger');
                } else if (typeof e === 'object' && e.status !== undefined) {
                    // if is an object, it's an error object
                    if (e.status === 500) {
                        setAlert("Ooops", "Server error, try again in some minutes", 'danger');
                        return;
                    }
                    setAlert("Ooops", JSON.stringify(e), 'danger');
                }
            });
            return;
        }
        if (action === "cancel") {
            let payload = {
                stage: "cancelled",
            };
            apiTriggerStep(uuid, payload).then((r) => {
                if (reloadData) {
                    reloadData();
                }
                setAlert(action, `Cancelled release "${r.release.version}"`, 'success');
            }).catch((e) => {
                setAlert("Cancel error", e, 'danger');
            });
            return;
        }
        if (action === "set_to_release") {
            setReleaseTagWithAlert(uuid, { to_release: true }, {
                reloadData,
                successTitle: action,
                successMessage: (r) => `Set release "${r.release.version}" as to release target`,
                errorTitle: "Set to release error",
            }).catch(() => {});
            return;
        }
        if (action === "remove_to_release") {
            setReleaseTagWithAlert(uuid, { to_release: false }, {
                reloadData,
                successTitle: action,
                successMessage: (r) => `Set release "${r.release.version}" as not to release target`,
                errorTitle: "Set to release error",
            }).catch(() => {});
            return;
        }
        if (action === "set_latest") {
            setReleaseTagWithAlert(uuid, { is_latest: true }, {
                reloadData,
                successTitle: action,
                successMessage: (r) => `Set release "${r.release.version}" as latest release`,
                errorTitle: "Set latest error",
            }).catch(() => {});
            return;
        }
    };

    const onDeleteConfirmed = (uuid) => {
        handleAction(uuid, "delete");
        setDialog({ open: false });
    };
    const handleDelete = (r) => {
        setDialog({
            open: true,
            title: "Delete release",
            message: `Are you sure you want to delete release "${r.version}"?`,
            onConfirm: () => onDeleteConfirmed(r.version),
            onCancel: () => setDialog({ open: false })
        });
    };

    const handleRelease = (r) => {
        setDialog({
            open: true,
            title: "Release",
            message: `Are you sure you want to trigger release "${r.version}"?`,
            onConfirm: () => {
                handleAction(r.version, "release");
                setDialog({ open: false });
            },
            onCancel: () => setDialog({ open: false })
        });
    };

    if (!release) {
        return <Stack></Stack>;
    }
    const buttonsForRelease = (r) => {
        let dir = direction !== undefined ? direction : "column";
        switch (release.status) {
            case 'planned':
                return (
                    <Stack direction={dir} spacing={2}>
                        <Button variant="contained" color="success" onClick={() => handleAction(r.version, "branch")}>Create branches</Button>
                        <Button variant="contained" color="error" onClick={() => handleAction(r.version, "delete")}>Delete</Button>
                    </Stack>
                );
            case 'cancelled':
                return (
                    <Stack direction={dir} spacing={2}>
                        <Button variant="contained" color="secondary" onClick={() => handleDelete(r)}>Delete</Button>
                    </Stack>
                );
            case 'branched':
                return (
                    <Stack direction={dir} spacing={2}>
                        <Button variant="contained" color="success" onClick={() => handleAction(r.version, "run_test")}>Run tests</Button>
                        {
                            release.to_release ?
                            <Button variant="contained" color="info" onClick={() => handleAction(r.version, "remove_to_release")}>Remove as to release target</Button>
                            :
                            <Button variant="contained" color="info" onClick={() => handleAction(r.version, "set_to_release")}>Set as to release target</Button>
                        }
                        <Button variant="contained" color="error" onClick={() => handleAction(r.version, "cancel")}>Cancel</Button>
                    </Stack>
                );
            case 'testing':
            case 'validating':
                const testValidation = checkTestsAreValid(release, user);
                return (
                    <Stack direction={dir} spacing={1}>
                        <Button variant="contained" color="info" onClick={() => handleAction(r.version, "run_test")}>Re-run tests</Button>
                        {
                            !release.to_release &&
                            <Button variant="contained" color="info" onClick={() => handleAction(r.version, "set_to_release")}>Set as to release target</Button>
                        }
                        <Button
                            variant="contained"
                            color="success"
                            onClick={() => handleRelease(r)}
                            // disabled={!testValidation.isValid}
                            title={!testValidation.isValid ? testValidation.message : "Release"}
                        >
                            Release
                        </Button>
                        {testValidation.message !== "" && (
                            <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                                {testValidation.message}
                            </Typography>
                        )}
                    </Stack>
                );
            case 'released':
                return (
                    <Stack direction={dir} spacing={2}>
                        {
                            !release.is_latest &&
                            <Button variant="contained" color="info" onClick={() => handleAction(r.version, "set_latest")}>Set as latest release</Button>
                        }
                    </Stack>
                );

            default:
                return (
                    <Stack direction={dir}>
                        <Button variant="contained" color="error" onClick={() => handleAction(r.version, "cancel")}>Cancel</Button>
                    </Stack>
                );
        };
    }
    return (
        <React.Fragment>
            {buttonsForRelease(release)}

            <ConfirmationDialog
                open={dialog.open}
                onCancel={dialog.onCancel}
                onConfirm={dialog.onConfirm}
                title={dialog.title}
                message={dialog.message}
            />
        </React.Fragment>
    )
};
